import { Suspense, useEffect, useMemo, useRef, useState } from 'react';

import { useQuery } from '@apollo/client/react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  LinearProgress,
  ListItemButton,
  Paper,
  Stack,
  TextField,
  type Theme,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { format, formatDistanceStrict, startOfDay } from 'date-fns';
import {
  ChevronDown,
  ChevronUp,
  ClipboardCheck,
  Clock,
  DoorClosed,
  DoorOpen,
  FileText,
  Hash,
  type LucideIcon,
  Mail,
  Phone,
  PhoneMissed,
  Plus,
  Sparkles,
  StickyNote,
  Tag,
  TriangleAlert,
  UserCheck,
  Users,
  X,
} from 'lucide-react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import {
  Activity_Types_Enum_Enum,
  type BuyerLeadDashboardActivityFragment,
  type BuyerLeadDashboardRowFragment,
  type Buyer_Leads_Bool_Exp,
  Doc_Template_Type_Enum_Enum,
  type ListingEmailCampaignActivityFragment,
  Order_By,
} from '@graphql/__generated__/graphql';
import { ActionsMenu } from '@modules/_shared/components/buyer-lead/BuyerLeadActionToolbar';
import { getEffectiveUser } from '@modules/_shared/components/buyer-lead/buyerLeadMlsFallback';
import { GET_BUYER_LEAD_STAGES } from '@modules/_shared/components/buyer-lead/buyerLeadQueries';
import {
  getBuyerLeadSourceMeta,
  isMatchingSource,
} from '@modules/_shared/components/buyer-lead/buyerLeadSource';
import { getBuyerLeadOutcome } from '@modules/_shared/components/buyer-lead/buyerLeadStatus';
import {
  BUYERS_SEARCH_PARAMS_SCOPE,
  parseBuyerLeadsWhereParam,
  withCurrentBuyersWhere,
} from '@modules/_shared/components/buyer-lead/buyerLeadUtils';
import { useDropRejectedBuyersWhere } from '@modules/_shared/components/buyer-lead/useDropRejectedBuyersWhere';
import { Drawer } from '@modules/_shared/components/drawer/Drawer';
import { DrawerTopBar } from '@modules/_shared/components/drawer/DrawerTopBar';
import type { QuickFilterDefinition } from '@modules/_shared/components/filters/Filters';
import { LocalFilters } from '@modules/_shared/components/filters/LocalFilters';
import { FilterChip } from '@modules/_shared/components/filters/quick-filters/FilterChip';
import { ListingAccordion } from '@modules/_shared/components/ListingAccordion';
import { TimeAgo } from '@modules/_shared/components/TimeAgo';
import { UserCard } from '@modules/_shared/components/UserCard';
import { IconBadge } from '@modules/_shared/shared/components/IconBadge';
import MetricItem from '@modules/_shared/shared/components/MetricItem';
import { activityNotePlainText } from '@modules/_shared/utils/activity';
import { subscribeFeedRefresh } from '@modules/_shared/utils/feed-refresh';
import { useScopedSearchParams } from '@modules/_shared/utils/navigation';
import {
  GET_LISTING_BUYERS_DASHBOARD,
  GET_LISTING_BUYER_LEADS_LIST,
  GET_LISTING_EMAIL_CAMPAIGNS,
  GET_LISTING_EMAIL_CAMPAIGN_RECIPIENTS,
} from '@modules/listings/listingBuyersDashboardQueries';
import { useAutoTour } from '@modules/product-updates/tours/TourProvider';
import { tourAnchor } from '@modules/product-updates/tours/tourTypes';
import { type Translate, useLocale } from '@shared/hooks/locale';
import { toGlobalId } from '@shared/utils/global-id';

import { GET_DOCUMENT_RECIPIENTS } from './brochureRecipientsQueries';
import { LISTING_BUYERS_ANCHORS } from './listingBuyersTour';

// Activity types that count as a schedulable "next activity" (mirrors the
// next_activity query fragment). Notes/emails aren't scheduled, so they're out.
const SCHEDULED_ACTIVITY_TYPES = [
  Activity_Types_Enum_Enum.Call,
  Activity_Types_Enum_Enum.Visit,
  Activity_Types_Enum_Enum.Task,
  Activity_Types_Enum_Enum.Assignment,
];
// Sentinel value for the "no upcoming activity" option in the Next activity filter.
const NEXT_ACTIVITY_NONE = 'none';

// Options of the Brochure filter. Only a send to a single To records the buyer
// as a recipient with a personal link (several To can't be attributed), so
// "not opened" only covers buyers who got one; everyone else is "no tracked
// link".
const BROCHURE_OPENED = 'opened';
const BROCHURE_NOT_OPENED = 'not_opened';
const BROCHURE_NO_LINK = 'no_link';

type ListingBuyersDashboardProps = {
  lotId: string;
  readOnly?: boolean;
  matchingButtonTo: { pathname: string; search: string };
  autoMatchingTo: { pathname: string; search: string };
  onOpenAddModal: () => void;
  selectedIds: string[];
  onToggleSelect: (id: string) => void;
  onSetSelectedIds: (ids: string[]) => void;
  onBuyerLeadsChange: (leads: BuyerLeadDashboardRowFragment[]) => void;
  // The listing, used as the email-template context for bulk actions inside the
  // matching-batch drawer.
  parent: React.ComponentProps<typeof ActionsMenu>['parent'];
  matchedStageId: string | undefined;
};

const MAX_MATCHING_BATCHES = 6;

export const ListingBuyersDashboard: React.FC<ListingBuyersDashboardProps> = ({
  lotId,
  readOnly = false,
  matchingButtonTo,
  autoMatchingTo,
  onOpenAddModal,
  selectedIds,
  onToggleSelect,
  onSetSelectedIds,
  onBuyerLeadsChange,
  parent,
  matchedStageId,
}) => {
  // Plays when the user arrives from "Show me" on a buyers entry.
  useAutoTour('listing-buyers');
  const { t, dateLocale } = useLocale();
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialSearchParams = useRef(searchParams);

  const {
    data,
    error,
    loading,
    refetch: refetchDashboard,
  } = useQuery(GET_LISTING_BUYERS_DASHBOARD, {
    variables: { id: lotId },
    fetchPolicy: 'cache-and-network',
  });
  const { data: stagesData, error: stagesError } = useQuery(
    GET_BUYER_LEAD_STAGES,
  );

  const stages = useMemo(
    () => stagesData?.lead_stages ?? [],
    [stagesData?.lead_stages],
  );
  const stageColumns = useMemo(
    () =>
      stages.map(stage => ({
        id: stage.id,
        label: stage.label ?? '',
        status: stage.status,
      })),
    [stages],
  );
  const lot = data?.lots_by_pk;
  const autoMatchingEnabled = lot?.enable_auto_matching ?? false;
  const autoStatusDot = (
    <Box
      sx={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        flexShrink: 0,
        bgcolor: autoMatchingEnabled ? 'success.main' : 'text.disabled',
      }}
    />
  );
  const autoStatusLabel = autoMatchingEnabled
    ? t('Auto-matching on')
    : t('Auto-matching off');
  const buyerLeads = useMemo(() => lot?.buyer_leads ?? [], [lot?.buyer_leads]);
  useEffect(() => {
    onBuyerLeadsChange(buyerLeads);
  }, [buyerLeads, onBuyerLeadsChange]);

  const offers = useMemo(() => lot?.offers ?? [], [lot?.offers]);
  const offersCount = offers.length;
  const offersByStatus = useMemo(() => {
    const counts = { accepted: 0, pending: 0, refused: 0 };
    for (const offer of offers) {
      if (offer.status != null && offer.status in counts) {
        counts[offer.status as keyof typeof counts] += 1;
      }
    }
    return counts;
  }, [offers]);

  const viewingsPending = data?.viewings_pending.aggregate?.count ?? 0;
  const viewingsDone = data?.viewings_done.aggregate?.count ?? 0;
  const viewingsCount = viewingsPending + viewingsDone;
  const leadsCount = buyerLeads.length;
  // Leads that have at least one unprocessed enquiry linked to them, so the
  // list row can flag it.
  const leadIdsWithUnprocessedEnquiry = useMemo(
    () =>
      new Set(
        (lot?.enquiries ?? [])
          .map(enquiry => enquiry.buyer_lead_id)
          .filter((id): id is string => id != null),
      ),
    [lot?.enquiries],
  );
  // Leads are ordered by updated_at desc, so derive "last added" from the max
  // created_at across all leads rather than the first row.
  const lastAddedAt = useMemo(
    () =>
      buyerLeads.reduce<string | undefined>(
        (max, lead) =>
          max == null || lead.created_at > max ? lead.created_at : max,
        undefined,
      ),
    [buyerLeads],
  );
  const lastAddedDistance = useMemo(
    () =>
      lastAddedAt == null
        ? undefined
        : formatDistanceStrict(new Date(lastAddedAt), new Date(), {
            locale: dateLocale,
          }),
    [lastAddedAt, dateLocale],
  );

  const openLead = (leadId: string) => {
    navigate({
      pathname: `./${leadId}`,
      search: withCurrentBuyersWhere(
        initialSearchParams.current,
        searchParams,
      ).toString(),
    });
  };

  // The buyer leads list uses the standard schema-driven filter system in local
  // mode: `LocalFilters` produces a Hasura `where` (no URL coupling) that drives
  // a dedicated server query. The other cards keep using the full set above, so
  // filtering only narrows the list.
  const [buyersSearchParams] = useScopedSearchParams(
    BUYERS_SEARCH_PARAMS_SCOPE,
  );
  const urlWhereParam = buyersSearchParams.get('where');
  const urlWhere = useMemo(
    () => parseBuyerLeadsWhereParam(urlWhereParam),
    [urlWhereParam],
  );
  const [filtersWhere, setFiltersWhere] =
    useState<Buyer_Leads_Bool_Exp>(urlWhere);
  useEffect(() => {
    setFiltersWhere(urlWhere);
  }, [urlWhere]);
  // "Offers" isn't a standard schema field (it's a count of the buyer's offers
  // on this listing), so it's tracked separately and folded into the where as
  // an aggregate-count predicate. Modelled as a min/max range like the standard
  // NumberFilter, but kept custom because the aggregate must be scoped to this
  // lot via `filter`, which the schema-driven NumberFilter can't express.
  const [offersMin, setOffersMin] = useState<number | null>(null);
  const [offersMax, setOffersMax] = useState<number | null>(null);
  // "Next activity" can't use the schema-driven StringFilter: buyer_leads has no
  // computed `next_activity` relationship (only `activities`), we want a fixed
  // option set (the scheduled types + "None"), and "None" needs `_not` semantics.
  // So it's tracked here and folded into the where as an `activities` predicate.
  const [nextActivity, setNextActivity] = useState<string[]>([]);
  const [overdueOnly, setOverdueOnly] = useState(false);
  // Brochure opens live in BigQuery, not in Postgres, so they can't be a
  // schema-driven filter. The document_recipients action returns who got a
  // tracked brochure link and how often they opened it; the selected options
  // are folded into the where as `user_id` sets.
  const [brochure, setBrochure] = useState<string[]>([]);
  const { data: brochureRecipientsData, error: brochureRecipientsError } =
    useQuery(GET_DOCUMENT_RECIPIENTS, {
      variables: {
        documentId: toGlobalId('Lot', lotId),
        documentType: Doc_Template_Type_Enum_Enum.LotBrochure,
      },
      skip: brochure.length === 0,
      fetchPolicy: 'cache-and-network',
    });
  const brochureRecipients =
    brochureRecipientsData?.document_recipients?.recipients;
  // The list waits for the recipients (pending); the loading bar only runs
  // while they're actually loading, so a failed action shows its error Alert
  // instead of an endless spinner. Clearing the chip and picking again retries.
  const brochurePending = brochure.length > 0 && brochureRecipients == null;
  const brochureLoading = brochurePending && brochureRecipientsError == null;
  const [showAllStages, setShowAllStages] = useState(false);
  const [showAllBatches, setShowAllBatches] = useState(false);
  const [showAllCampaigns, setShowAllCampaigns] = useState(false);
  const hasActiveFilters =
    Object.keys(filtersWhere).length > 0 ||
    offersMin != null ||
    offersMax != null ||
    nextActivity.length > 0 ||
    overdueOnly ||
    brochure.length > 0;

  const listWhere = useMemo<Buyer_Leads_Bool_Exp>(() => {
    const clauses: Buyer_Leads_Bool_Exp[] = [
      { lot_id: { _eq: lotId } },
      filtersWhere,
    ];
    if (offersMin != null || offersMax != null) {
      clauses.push({
        user: {
          buyer_offers_aggregate: {
            count: {
              filter: { lot_id: { _eq: lotId } },
              predicate: {
                ...(offersMin != null && { _gte: offersMin }),
                ...(offersMax != null && { _lte: offersMax }),
              },
            },
          },
        },
      });
    }
    if (nextActivity.length > 0) {
      const selectedTypes = nextActivity.filter(
        (value): value is Activity_Types_Enum_Enum =>
          value !== NEXT_ACTIVITY_NONE,
      );
      const ors: Buyer_Leads_Bool_Exp[] = [];
      if (selectedTypes.length > 0) {
        // Has an upcoming (not-done) activity of one of the selected types.
        ors.push({
          activities: {
            done: { _eq: false },
            activity_type: { _in: selectedTypes },
          },
        });
      }
      if (nextActivity.includes(NEXT_ACTIVITY_NONE)) {
        // No upcoming scheduled activity at all.
        ors.push({
          _not: {
            activities: {
              done: { _eq: false },
              activity_type: { _in: SCHEDULED_ACTIVITY_TYPES },
            },
          },
        });
      }
      clauses.push(ors.length === 1 ? ors[0] : { _or: ors });
    }
    if (overdueOnly) {
      clauses.push({
        activities: {
          done: { _eq: false },
          is_overdue: { _eq: true },
          activity_type: { _in: SCHEDULED_ACTIVITY_TYPES },
        },
      });
    }
    if (brochure.length > 0) {
      const recipients = brochureRecipients ?? [];
      const openedIds = recipients
        .filter(recipient => recipient.total_opens > 0)
        .map(recipient => recipient.user_id);
      const notOpenedIds = recipients
        .filter(recipient => recipient.total_opens === 0)
        .map(recipient => recipient.user_id);
      const ors: Buyer_Leads_Bool_Exp[] = [];
      if (brochure.includes(BROCHURE_OPENED)) {
        ors.push({ user_id: { _in: openedIds } });
      }
      if (brochure.includes(BROCHURE_NOT_OPENED)) {
        ors.push({ user_id: { _in: notOpenedIds } });
      }
      if (brochure.includes(BROCHURE_NO_LINK)) {
        ors.push({
          user_id: {
            _nin: recipients.map(recipient => recipient.user_id),
          },
        });
      }
      clauses.push(ors.length === 1 ? ors[0] : { _or: ors });
    }
    return { _and: clauses };
  }, [
    lotId,
    filtersWhere,
    offersMin,
    offersMax,
    nextActivity,
    overdueOnly,
    brochure,
    brochureRecipients,
  ]);

  const {
    data: listData,
    loading: listLoading,
    refetch: refetchList,
    error: listError,
  } = useQuery(GET_LISTING_BUYER_LEADS_LIST, {
    variables: { where: listWhere },
    // Wait for the brochure recipients, or the list would briefly show
    // everyone as "no tracked link".
    skip: !hasActiveFilters || brochurePending,
    fetchPolicy: 'network-only',
  });
  const droppingRejectedWhere = useDropRejectedBuyersWhere(listError);
  const listQueryError =
    listError ?? (brochure.length > 0 ? brochureRecipientsError : undefined);
  const filteredLeads = hasActiveFilters
    ? listData?.buyer_leads ?? []
    : buyerLeads;

  // "Select all" affects exactly what's currently displayed (i.e. the filtered
  // list), unioning/removing those ids from any existing selection.
  const filteredLeadIds = filteredLeads.map(lead => lead.id);
  const allFilteredSelected =
    filteredLeadIds.length > 0 &&
    filteredLeadIds.every(id => selectedIds.includes(id));
  const someFilteredSelected = filteredLeadIds.some(id =>
    selectedIds.includes(id),
  );
  const toggleSelectAll = () => {
    if (allFilteredSelected) {
      onSetSelectedIds(selectedIds.filter(id => !filteredLeadIds.includes(id)));
    } else {
      onSetSelectedIds(
        Array.from(new Set([...selectedIds, ...filteredLeadIds])),
      );
    }
  };

  const {
    data: emailData,
    error: emailError,
    refetch: refetchCampaigns,
  } = useQuery<
    { activities: EmailCampaignSummaryActivity[] },
    { lotId: string }
  >(GET_LISTING_EMAIL_CAMPAIGNS, {
    variables: { lotId },
    fetchPolicy: 'cache-and-network',
  });

  // Activity actions (scheduling a visit, logging a call, …) happen in the
  // buyer-lead drawer and broadcast a global feed-refresh event. Re-fetch the
  // dashboard + list + mass-email campaigns so new activity shows without a
  // manual reload.
  useEffect(() => {
    const unsubscribe = subscribeFeedRefresh(() => {
      void refetchDashboard();
      void refetchCampaigns();
      if (hasActiveFilters) {
        void refetchList();
      }
    });
    return unsubscribe;
  }, [refetchDashboard, refetchList, refetchCampaigns, hasActiveFilters]);

  const quickFilters = useMemo<QuickFilterDefinition[]>(
    () => [
      {
        label: t('Created'),
        path: ['buyer_leads_bool_exp', 'created_at'],
      },
      {
        label: t('Updated'),
        path: ['buyer_leads_bool_exp', 'updated_at'],
      },
      {
        label: t('Stage'),
        path: ['buyer_leads_bool_exp', 'lead_stage', 'name', '_in'],
        displayedColumn: 'label',
        filter: {
          path: ['lead_stages', 'pipeline'],
          value: { name: { _eq: 'house_sales' }, lead_type: { _eq: 'buyer' } },
        },
        orderBy: { order_nr: Order_By.Asc },
      },
      {
        label: t('Status'),
        path: ['buyer_leads_bool_exp', 'lead_stage', 'status', '_in'],
      },
      {
        label: t('Source'),
        path: ['buyer_leads_bool_exp', 'source', '_in'],
      },
    ],
    [t],
  );

  // All stages in pipeline order with their lead counts and status (won/lost/
  // active), plus the active subset (count > 0) and the largest count so bars
  // can be scaled relative to it.
  const pipeline = useMemo(() => {
    const countByStage = new Map<string, number>();
    for (const lead of buyerLeads) {
      if (lead.stage_id != null) {
        countByStage.set(
          lead.stage_id,
          (countByStage.get(lead.stage_id) ?? 0) + 1,
        );
      }
    }
    const allRows = stages.map(stage => ({
      id: stage.id,
      label: stage.label ?? stage.name ?? '',
      status: stage.status ?? null,
      count: countByStage.get(stage.id) ?? 0,
    }));
    const activeRows = allRows.filter(stage => stage.count > 0);
    const maxCount = activeRows.reduce(
      (max, stage) => Math.max(max, stage.count),
      0,
    );
    const totalCount = activeRows.reduce((sum, stage) => sum + stage.count, 0);
    return {
      allRows,
      activeRows,
      maxCount,
      totalCount,
      hasHiddenStages: allRows.length > activeRows.length,
    };
  }, [buyerLeads, stages]);

  const allMatchingBatches = useMemo(() => {
    // Group by day *and* source so auto-matching and manual matching runs on
    // the same day stay separate, distinct batches.
    const byKey = new Map<string, MatchingBatch>();
    for (const lead of buyerLeads) {
      if (!isMatchingSource(lead.source)) {
        continue;
      }
      const source = lead.source ?? 'matching';
      const dayKey = startOfDay(new Date(lead.created_at)).getTime();
      const key = `${dayKey}:${source}`;
      const existing = byKey.get(key);
      if (existing == null) {
        byKey.set(key, {
          key,
          dayKey,
          source,
          date: lead.created_at,
          leads: [lead],
        });
      } else {
        existing.leads.push(lead);
        if (
          new Date(lead.created_at).getTime() >
          new Date(existing.date).getTime()
        ) {
          existing.date = lead.created_at;
        }
      }
    }
    return Array.from(byKey.values()).sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
    );
  }, [buyerLeads]);
  const matchingBatches = useMemo(
    () => allMatchingBatches.slice(0, MAX_MATCHING_BATCHES),
    [allMatchingBatches],
  );
  const hiddenMatchingBatchesCount =
    allMatchingBatches.length - matchingBatches.length;

  const [selectedBatchKey, setSelectedBatchKey] = useState<string | null>(null);
  const selectedBatch =
    allMatchingBatches.find(batch => batch.key === selectedBatchKey) ?? null;

  // Mass-email activity for this listing, grouped client-side by campaign into
  // batches (each "Run matching → email these buyers" produces one campaign).
  const allEmailCampaigns = useMemo(() => {
    const byCampaign = new Map<string, EmailCampaignBatch>();
    for (const activity of emailData?.activities ?? []) {
      const message = activity.email_message;
      const campaignId = message?.campaign_id;
      if (message == null || campaignId == null) {
        continue;
      }
      let batch = byCampaign.get(campaignId);
      if (batch == null) {
        batch = {
          campaignId,
          date: message.created_at,
          subject: message.subject ?? null,
          activityCount: 0,
          counts: { sent: 0, error: 0, pending: 0 },
        };
        byCampaign.set(campaignId, batch);
      }
      batch.activityCount += 1;
      const status = message.status;
      if (status === 'sent' || status === 'error' || status === 'pending') {
        batch.counts[status] += 1;
      }
      if (
        new Date(message.created_at).getTime() > new Date(batch.date).getTime()
      ) {
        batch.date = message.created_at;
        batch.subject = message.subject ?? batch.subject;
      }
    }
    return Array.from(byCampaign.values()).sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
    );
  }, [emailData?.activities]);
  const emailCampaigns = useMemo(
    () => allEmailCampaigns.slice(0, MAX_MATCHING_BATCHES),
    [allEmailCampaigns],
  );
  const hiddenEmailCampaignsCount =
    allEmailCampaigns.length - emailCampaigns.length;

  const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(
    null,
  );
  const selectedCampaign =
    allEmailCampaigns.find(
      campaign => campaign.campaignId === selectedCampaignId,
    ) ?? null;

  if (error != null) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        <pre>{JSON.stringify(error, null, 2)}</pre>
      </Alert>
    );
  }

  const isEmpty = !loading && leadsCount === 0;

  return (
    <Box sx={{ flex: 1, minHeight: 0, width: '100%', overflowY: 'auto' }}>
      {(loading || listLoading || brochureLoading) && (
        <LinearProgress
          sx={{ position: 'absolute', top: 0, width: '100%', zIndex: 2000 }}
        />
      )}
      <Box sx={{ px: { xs: 0, md: 2 }, py: 2, width: '100%' }}>
        {isEmpty ? (
          <ListingAccordion title={t('Buyer leads')} defaultExpanded>
            <Stack sx={{ alignItems: 'center', textAlign: 'center', py: 3 }}>
              <IconBadge icon={Users} color="primary.main" size="lg" />
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 1.5 }}>
                {t('No buyers yet')}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', mt: 0.5, maxWidth: 360 }}
              >
                {t(
                  'Run matching to find buyers in your database, or add a buyer lead manually.',
                )}
              </Typography>
              {!readOnly && (
                <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                  <Button
                    variant="outlined"
                    startIcon={<Plus size={16} />}
                    onClick={onOpenAddModal}
                  >
                    {t('Add buyer lead')}
                  </Button>
                  <Button
                    variant="contained"
                    disableElevation
                    component={Link}
                    to={matchingButtonTo}
                    startIcon={<Sparkles size={16} />}
                  >
                    {t('Match manually')}
                  </Button>
                </Stack>
              )}
            </Stack>
          </ListingAccordion>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 2,
              alignItems: 'start',
            }}
          >
            <Stack
              sx={{
                minWidth: 0,
                '& .MuiAccordion-root.Mui-expanded': { my: 1 },
              }}
            >
              <ListingAccordion title={t('Overview')} defaultExpanded>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: 2,
                  }}
                >
                  <MetricItem
                    icon={<IconBadge icon={Users} color="primary.main" />}
                    value={leadsCount}
                    label={t('Buyer leads')}
                  />
                  <MetricItem
                    icon={<IconBadge icon={Clock} color="warning.main" />}
                    value={lastAddedDistance}
                    label={t('Since last lead added')}
                  />
                  <MetricItem
                    icon={<IconBadge icon={DoorOpen} color="info.main" />}
                    value={viewingsCount}
                    label={t('Viewings')}
                    tooltip={
                      viewingsCount > 0
                        ? {
                            title: t('Viewings'),
                            description: (
                              <StatusBreakdown
                                rows={[
                                  {
                                    label: t('Pending'),
                                    value: viewingsPending,
                                  },
                                  { label: t('Done'), value: viewingsDone },
                                ]}
                              />
                            ),
                          }
                        : undefined
                    }
                  />
                  <MetricItem
                    icon={<IconBadge icon={Tag} color="success.main" />}
                    value={offersCount}
                    label={t('Offers')}
                    tooltip={
                      offersCount > 0
                        ? {
                            title: t('Offers'),
                            description: (
                              <StatusBreakdown
                                rows={[
                                  {
                                    label: t('Accepted'),
                                    value: offersByStatus.accepted,
                                  },
                                  {
                                    label: t('Pending'),
                                    value: offersByStatus.pending,
                                  },
                                  {
                                    label: t('Refused'),
                                    value: offersByStatus.refused,
                                  },
                                ]}
                              />
                            ),
                          }
                        : undefined
                    }
                  />
                </Box>
              </ListingAccordion>

              <ListingAccordion
                title={t('Buyer leads ({{leadsCount}})', {
                  leadsCount: filteredLeads.length,
                })}
                defaultExpanded
                disablePadding
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    px: 1,
                    overflowX: 'auto',
                    '& > *': { flexShrink: 0 },
                  }}
                >
                  <LocalFilters
                    where={filtersWhere}
                    setWhere={setFiltersWhere}
                    quickFilters={quickFilters}
                  />
                  {/* Custom chips share LocalFilters' 8px rhythm: its inner row
                      already pads 8px after the last schema chip, so this group
                      adds no left padding and only spaces its own chips by gap. */}
                  <Box
                    {...tourAnchor(LISTING_BUYERS_ANCHORS.filters)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      pr: 1,
                    }}
                  >
                    <NextActivityFilterChip
                      value={nextActivity}
                      onChange={setNextActivity}
                    />
                    <OverdueFilterChip
                      value={overdueOnly}
                      onChange={setOverdueOnly}
                    />
                    <BrochureFilterChip
                      value={brochure}
                      onChange={setBrochure}
                    />
                    <OffersFilterChip
                      min={offersMin}
                      max={offersMax}
                      onChange={(min, max) => {
                        setOffersMin(min);
                        setOffersMax(max);
                      }}
                    />
                  </Box>
                </Box>
                <Divider />
                {!readOnly && filteredLeads.length > 0 && (
                  <>
                    <ListItemButton
                      {...tourAnchor(LISTING_BUYERS_ANCHORS.selectAll)}
                      onClick={toggleSelectAll}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 2,
                        py: 0.5,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      <Checkbox
                        checked={allFilteredSelected}
                        indeterminate={
                          someFilteredSelected && !allFilteredSelected
                        }
                        tabIndex={-1}
                        disableRipple
                        size="small"
                        sx={{ p: 0.5 }}
                      />
                      <Typography
                        variant="body2"
                        sx={{ fontSize: 13, fontWeight: 500 }}
                      >
                        {allFilteredSelected
                          ? t('Deselect all')
                          : t('Select all')}
                      </Typography>
                    </ListItemButton>
                    <Divider />
                  </>
                )}
                {listQueryError != null &&
                hasActiveFilters &&
                !droppingRejectedWhere ? (
                  <Alert severity="error" sx={{ m: 2 }}>
                    {listQueryError.message}
                  </Alert>
                ) : filteredLeads.length === 0 ? (
                  listLoading || brochureLoading ? null : (
                    <Typography
                      variant="body2"
                      sx={{ color: 'text.secondary', px: 2, py: 2 }}
                    >
                      {hasActiveFilters
                        ? t('No buyer leads match the selected filters.')
                        : t('No buyer leads yet on this listing.')}
                    </Typography>
                  )
                ) : (
                  <Stack divider={<Divider />}>
                    {filteredLeads.map(lead => (
                      <BuyerLeadRow
                        key={lead.id}
                        lead={lead}
                        stageStatus={
                          stageColumns.find(stage => stage.id === lead.stage_id)
                            ?.status
                        }
                        hasUnprocessedEnquiry={leadIdsWithUnprocessedEnquiry.has(
                          lead.id,
                        )}
                        selectable={!readOnly}
                        selected={selectedIds.includes(lead.id)}
                        onToggleSelect={() => onToggleSelect(lead.id)}
                        onClick={() => openLead(lead.id)}
                      />
                    ))}
                  </Stack>
                )}
              </ListingAccordion>
            </Stack>

            <Stack
              sx={{
                minWidth: 0,
                '& .MuiAccordion-root.Mui-expanded': { my: 1 },
              }}
            >
              <ListingAccordion title={t('Pipeline')} defaultExpanded>
                {stagesError != null ? (
                  <Alert severity="error" sx={{ m: 2 }}>
                    {stagesError.message}
                  </Alert>
                ) : pipeline.totalCount === 0 ? (
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {t('No buyer leads yet on this listing.')}
                  </Typography>
                ) : (
                  <Stack spacing={1.5}>
                    {(showAllStages
                      ? pipeline.allRows
                      : pipeline.activeRows
                    ).map(stage => {
                      const pct =
                        pipeline.maxCount > 0
                          ? (stage.count / pipeline.maxCount) * 100
                          : 0;
                      const barColor =
                        stage.status === 'won'
                          ? theme.palette.success.main
                          : stage.status === 'lost'
                          ? theme.palette.error.main
                          : theme.palette.primary.main;
                      return (
                        <Box key={stage.id}>
                          <Stack
                            direction="row"
                            sx={{
                              alignItems: 'baseline',
                              justifyContent: 'space-between',
                              gap: 1,
                              mb: 0.5,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500 }}
                              noWrap
                            >
                              {stage.label}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 700, flexShrink: 0 }}
                            >
                              {stage.count}
                            </Typography>
                          </Stack>
                          <Box
                            sx={{
                              height: 8,
                              borderRadius: 1,
                              overflow: 'hidden',
                              bgcolor: alpha(barColor, 0.12),
                            }}
                          >
                            <Box
                              sx={{
                                height: '100%',
                                width: `${Math.max(
                                  pct,
                                  stage.count > 0 ? 6 : 0,
                                )}%`,
                                borderRadius: 1,
                                bgcolor: barColor,
                              }}
                            />
                          </Box>
                        </Box>
                      );
                    })}
                    {pipeline.hasHiddenStages && (
                      <Button
                        size="small"
                        onClick={() => setShowAllStages(prev => !prev)}
                        endIcon={
                          showAllStages ? (
                            <ChevronUp size={14} />
                          ) : (
                            <ChevronDown size={14} />
                          )
                        }
                        sx={{ alignSelf: 'center', mt: 0.5 }}
                      >
                        {showAllStages ? t('Show fewer') : t('Show all stages')}
                      </Button>
                    )}
                  </Stack>
                )}
              </ListingAccordion>

              <ListingAccordion
                title={t('Matching')}
                defaultExpanded
                disablePadding
                headerAction={
                  <Stack
                    {...tourAnchor(LISTING_BUYERS_ANCHORS.matching)}
                    direction="row"
                    spacing={1}
                    sx={{ alignItems: 'center' }}
                  >
                    {readOnly ? (
                      <Stack
                        direction="row"
                        spacing={0.75}
                        sx={{
                          alignItems: 'center',
                          px: 1,
                          py: 0.5,
                          border: 1,
                          borderColor: 'divider',
                          borderRadius: 1,
                        }}
                      >
                        {autoStatusDot}
                        <Typography
                          variant="caption"
                          sx={{ color: 'text.secondary', fontWeight: 500 }}
                        >
                          {autoStatusLabel}
                        </Typography>
                      </Stack>
                    ) : (
                      <Tooltip title={t('Auto-matching settings')}>
                        <Button
                          component={Link}
                          to={autoMatchingTo}
                          size="small"
                          variant="outlined"
                          color="inherit"
                          startIcon={autoStatusDot}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 500,
                            color: 'text.secondary',
                            borderColor: 'divider',
                            '&:hover': {
                              borderColor: 'text.secondary',
                              bgcolor: 'action.hover',
                            },
                          }}
                        >
                          {autoStatusLabel}
                        </Button>
                      </Tooltip>
                    )}
                    {!readOnly && (
                      <Button
                        size="small"
                        variant="contained"
                        disableElevation
                        component={Link}
                        to={matchingButtonTo}
                        startIcon={<Sparkles size={14} />}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                      >
                        {t('Match manually')}
                      </Button>
                    )}
                  </Stack>
                }
              >
                {matchingBatches.length === 0 ? (
                  <Typography
                    variant="body2"
                    sx={{ color: 'text.secondary', px: 2, py: 2 }}
                  >
                    {t('No matching has been run on this listing yet.')}
                  </Typography>
                ) : (
                  <Stack divider={<Divider />}>
                    {(showAllBatches
                      ? allMatchingBatches
                      : matchingBatches
                    ).map(batch => {
                      const meta = getBuyerLeadSourceMeta(batch.source, t);
                      return (
                        <ListItemButton
                          key={batch.key}
                          onClick={() => setSelectedBatchKey(batch.key)}
                          sx={{
                            alignItems: 'center',
                            gap: 1.5,
                            px: 2,
                            py: 1.25,
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'action.hover' },
                          }}
                        >
                          <IconBadge
                            icon={meta.Icon}
                            color="primary.main"
                            size="sm"
                          />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" noWrap>
                              {meta.label}
                            </Typography>
                            <Typography
                              variant="caption"
                              noWrap
                              sx={{ display: 'block', color: 'text.secondary' }}
                            >
                              {t('{{count}} buyers matched', {
                                count: batch.leads.length,
                              })}
                            </Typography>
                          </Box>
                          <Box
                            component="span"
                            sx={{
                              color: 'text.secondary',
                              fontSize: 12,
                              flexShrink: 0,
                            }}
                          >
                            <TimeAgo dateString={batch.date} addSuffix />
                          </Box>
                        </ListItemButton>
                      );
                    })}
                    {hiddenMatchingBatchesCount > 0 && (
                      <Button
                        size="small"
                        onClick={() => setShowAllBatches(prev => !prev)}
                        endIcon={
                          showAllBatches ? (
                            <ChevronUp size={14} />
                          ) : (
                            <ChevronDown size={14} />
                          )
                        }
                        sx={{ alignSelf: 'center', my: 0.5 }}
                      >
                        {showAllBatches ? t('Show less') : t('Show more')}
                      </Button>
                    )}
                  </Stack>
                )}
              </ListingAccordion>

              <ListingAccordion
                title={t('Mass emails')}
                defaultExpanded
                disablePadding
              >
                {emailError != null ? (
                  <Alert severity="error" sx={{ m: 2 }}>
                    {emailError.message}
                  </Alert>
                ) : emailCampaigns.length === 0 ? (
                  <Typography
                    variant="body2"
                    sx={{ color: 'text.secondary', px: 2, py: 2 }}
                  >
                    {t(
                      'No mass email campaigns have been sent to buyer leads on this listing yet.',
                    )}
                  </Typography>
                ) : (
                  <Stack divider={<Divider />}>
                    {(showAllCampaigns
                      ? allEmailCampaigns
                      : emailCampaigns
                    ).map(campaign => (
                      <ListItemButton
                        key={campaign.campaignId}
                        onClick={() =>
                          setSelectedCampaignId(campaign.campaignId)
                        }
                        sx={{
                          alignItems: 'center',
                          gap: 1.5,
                          px: 2,
                          py: 1.25,
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'action.hover' },
                        }}
                      >
                        <IconBadge icon={Mail} color="primary.main" size="sm" />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" noWrap>
                            {t('{{count}} emails', {
                              count: campaign.activityCount,
                            })}
                          </Typography>
                          <EmailStatusCounts counts={campaign.counts} />
                        </Box>
                        <Box
                          component="span"
                          sx={{
                            color: 'text.secondary',
                            fontSize: 12,
                            flexShrink: 0,
                          }}
                        >
                          <TimeAgo dateString={campaign.date} addSuffix />
                        </Box>
                      </ListItemButton>
                    ))}
                    {hiddenEmailCampaignsCount > 0 && (
                      <Button
                        size="small"
                        onClick={() => setShowAllCampaigns(prev => !prev)}
                        endIcon={
                          showAllCampaigns ? (
                            <ChevronUp size={14} />
                          ) : (
                            <ChevronDown size={14} />
                          )
                        }
                        sx={{ alignSelf: 'center', my: 0.5 }}
                      >
                        {showAllCampaigns ? t('Show less') : t('Show more')}
                      </Button>
                    )}
                  </Stack>
                )}
              </ListingAccordion>
            </Stack>
          </Box>
        )}
      </Box>
      {selectedBatch != null && (
        <MatchingBatchDrawer
          batch={selectedBatch}
          onClose={() => setSelectedBatchKey(null)}
          unprocessedLeadIds={leadIdsWithUnprocessedEnquiry}
          onOpenLead={openLead}
          parent={parent}
          stageColumns={stageColumns}
          matchedStageId={matchedStageId}
          readOnly={readOnly}
        />
      )}
      {selectedCampaign != null && (
        <EmailCampaignDrawer
          campaign={selectedCampaign}
          lotId={lotId}
          onClose={() => setSelectedCampaignId(null)}
          onOpenLead={openLead}
        />
      )}
    </Box>
  );
};

type MatchingBatch = {
  key: string;
  dayKey: number;
  source: string;
  date: string;
  leads: BuyerLeadDashboardRowFragment[];
};

// Per-status counters shown in a MetricItem info tooltip.
const StatusBreakdown: React.FC<{
  rows: { label: string; value: number }[];
}> = ({ rows }) => (
  <Stack spacing={0.25} sx={{ minWidth: 120 }}>
    {rows.map(row => (
      <Box
        key={row.label}
        sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}
      >
        <span>{row.label}</span>
        <span style={{ fontWeight: 600 }}>{row.value}</span>
      </Box>
    ))}
  </Stack>
);

type EmailStatus = 'sent' | 'error' | 'pending';

type EmailCampaignSummaryActivity = {
  email_message?: {
    id: string;
    campaign_id?: string | null;
    status: string;
    subject?: string | null;
    created_at: string;
  } | null;
};

type EmailCampaignBatch = {
  campaignId: string;
  date: string;
  subject: string | null;
  activityCount: number;
  counts: Record<EmailStatus, number>;
};
type EmailCampaignRecipientsData = {
  activities: ListingEmailCampaignActivityFragment[];
};

const statusPillSx = (color: 'success' | 'error' | 'warning') => ({
  display: 'inline-flex',
  alignItems: 'center',
  width: 'fit-content',
  px: 0.75,
  py: '1px',
  borderRadius: 1,
  fontSize: 11,
  fontWeight: 600,
  color: `${color}.main`,
  bgcolor: (theme: Theme) => alpha(theme.palette[color].main, 0.12),
});

// "Offers" filter — a min/max range over the count of the buyer's offers on
// this listing. Mirrors the standard NumberFilter UI, but kept custom because
// it's an aggregate-count predicate (scoped to this lot), not a schema field.
const OffersFilterChip: React.FC<{
  min: number | null;
  max: number | null;
  onChange: (min: number | null, max: number | null) => void;
}> = ({ min, max, onChange }) => {
  const { t, locale } = useLocale();
  const [draftMin, setDraftMin] = useState<number | null>(min);
  const [draftMax, setDraftMax] = useState<number | null>(max);

  useEffect(() => {
    setDraftMin(min);
    setDraftMax(max);
  }, [min, max]);

  const label = useMemo(() => {
    const base = t('Offers');
    if (min != null && max != null) {
      return `${base}: ${min.toLocaleString(locale)} - ${max.toLocaleString(
        locale,
      )}`;
    }
    if (min != null) {
      return `${base}: ≥ ${min.toLocaleString(locale)}`;
    }
    if (max != null) {
      return `${base}: ≤ ${max.toLocaleString(locale)}`;
    }
    return base;
  }, [t, locale, min, max]);

  const parseValue = (value: string) => {
    if (value === '') {
      return null;
    }
    const parsed = Number(value);
    return isNaN(parsed) ? null : parsed;
  };

  return (
    <FilterChip
      label={label}
      icon={<Hash size={16} />}
      selected={min != null || max != null}
      onDelete={
        min != null || max != null ? () => onChange(null, null) : undefined
      }
      renderFilter={({ handleClose }) => (
        <Paper
          elevation={0}
          sx={{
            border: '1px solid',
            borderColor: 'grey.300',
            borderRadius: 2,
            boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
          }}
        >
          <form
            onSubmit={event => {
              event.preventDefault();
              onChange(draftMin, draftMax);
              handleClose();
            }}
          >
            <Box
              sx={{
                p: 1.5,
                display: 'flex',
                flexDirection: 'column',
                rowGap: 1,
              }}
            >
              <TextField
                autoFocus
                variant="outlined"
                placeholder={t('Min')}
                type="number"
                size="small"
                value={draftMin ?? ''}
                onChange={event => setDraftMin(parseValue(event.target.value))}
                sx={{
                  minWidth: 150,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'grey.300',
                  },
                }}
              />
              <TextField
                variant="outlined"
                placeholder={t('Max')}
                type="number"
                size="small"
                value={draftMax ?? ''}
                onChange={event => setDraftMax(parseValue(event.target.value))}
                sx={{
                  minWidth: 150,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'grey.300',
                  },
                }}
              />
              <Button size="small" type="submit">
                {t('Apply')}
              </Button>
            </Box>
          </form>
        </Paper>
      )}
    />
  );
};

// Multi-select chip over a fixed option list, for filters the schema-driven
// LocalFilters can't express.
const OptionsFilterChip: React.FC<{
  label: string;
  icon: React.ReactElement;
  options: { value: string; label: string }[];
  value: string[];
  onChange: (value: string[]) => void;
}> = ({ label, icon, options, value, onChange }) => {
  const selectedLabels = options
    .filter(option => value.includes(option.value))
    .map(option => option.label);

  const toggle = (optionValue: string) => {
    onChange(
      value.includes(optionValue)
        ? value.filter(item => item !== optionValue)
        : [...value, optionValue],
    );
  };

  return (
    <FilterChip
      label={
        selectedLabels.length === 0
          ? label
          : `${label}: ${selectedLabels.join(', ')}`
      }
      icon={icon}
      selected={value.length > 0}
      onDelete={value.length > 0 ? () => onChange([]) : undefined}
      renderFilter={() => (
        <Paper
          elevation={0}
          sx={{
            border: '1px solid',
            borderColor: 'grey.300',
            borderRadius: 2,
            boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
          }}
        >
          <Stack sx={{ p: 1, minWidth: 180 }}>
            {options.map(option => (
              <FormControlLabel
                key={option.value}
                control={
                  <Checkbox
                    size="small"
                    checked={value.includes(option.value)}
                    onChange={() => toggle(option.value)}
                  />
                }
                label={option.label}
              />
            ))}
          </Stack>
        </Paper>
      )}
    />
  );
};

const NextActivityFilterChip: React.FC<{
  value: string[];
  onChange: (value: string[]) => void;
}> = ({ value, onChange }) => {
  const { t } = useLocale();

  const options = useMemo(
    () => [
      { value: Activity_Types_Enum_Enum.Call as string, label: t('Call') },
      { value: Activity_Types_Enum_Enum.Visit as string, label: t('Visit') },
      { value: Activity_Types_Enum_Enum.Task as string, label: t('Task') },
      {
        value: Activity_Types_Enum_Enum.Assignment as string,
        label: t('Assignment'),
      },
      { value: NEXT_ACTIVITY_NONE, label: t('None') },
    ],
    [t],
  );

  return (
    <OptionsFilterChip
      label={t('Next activity')}
      icon={<Clock size={16} />}
      options={options}
      value={value}
      onChange={onChange}
    />
  );
};

const BrochureFilterChip: React.FC<{
  value: string[];
  onChange: (value: string[]) => void;
}> = ({ value, onChange }) => {
  const { t } = useLocale();

  const options = useMemo(
    () => [
      { value: BROCHURE_OPENED, label: t('Opened') },
      { value: BROCHURE_NOT_OPENED, label: t('Not opened') },
      { value: BROCHURE_NO_LINK, label: t('No tracked link') },
    ],
    [t],
  );

  return (
    <OptionsFilterChip
      label={t('Brochure')}
      icon={<FileText size={16} />}
      options={options}
      value={value}
      onChange={onChange}
    />
  );
};

const OverdueFilterChip: React.FC<{
  value: boolean;
  onChange: (value: boolean) => void;
}> = ({ value, onChange }) => {
  const { t } = useLocale();
  return (
    <Chip
      icon={<TriangleAlert size={16} />}
      label={t('Has overdue activity')}
      variant="outlined"
      size="medium"
      color={value ? 'primary' : 'default'}
      onClick={() => onChange(!value)}
      onDelete={value ? () => onChange(false) : undefined}
      deleteIcon={<X size={14} />}
      sx={{
        cursor: 'pointer',
        fontWeight: 500,
        borderRadius: 2,
        backgroundColor: theme =>
          `${theme.vars?.palette.background.paper} !important`,
      }}
    />
  );
};

const BuyerLeadRow: React.FC<{
  lead: BuyerLeadDashboardRowFragment;
  stageStatus: string | null | undefined;
  hasUnprocessedEnquiry: boolean;
  selectable: boolean;
  selected: boolean;
  onToggleSelect: () => void;
  onClick: () => void;
}> = ({
  lead,
  stageStatus,
  hasUnprocessedEnquiry,
  selectable,
  selected,
  onToggleSelect,
  onClick,
}) => {
  const { t, dateLocale } = useLocale();
  const effectiveUser = getEffectiveUser(lead);
  const source = getBuyerLeadSourceMeta(lead.source, t);
  const SourceIcon = source.Icon;
  const { isWon, isLost } = getBuyerLeadOutcome(stageStatus);

  // Only surface terminal statuses; "active" is the default and needs no chip.
  const statusChip = isWon
    ? { label: t('Won'), color: 'success' as const }
    : isLost
    ? { label: t('Lost'), color: 'error' as const }
    : null;

  const formatDate = (value: string) =>
    format(new Date(value), 'PPp', { locale: dateLocale });

  const unprocessedEnquiryChip = hasUnprocessedEnquiry ? (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: 0.75,
        py: '1px',
        borderRadius: 1,
        fontSize: 11,
        fontWeight: 600,
        whiteSpace: 'nowrap',
        color: 'error.main',
        bgcolor: theme => alpha(theme.palette.error.main, 0.12),
      }}
    >
      <Mail size={12} />
      {t('Unprocessed enquiry')}
    </Box>
  ) : undefined;

  return (
    <ListItemButton
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        px: 2,
        py: 1,
        cursor: 'pointer',
        bgcolor: selected ? 'action.selected' : undefined,
        '&:hover': { bgcolor: selected ? 'action.selected' : 'action.hover' },
      }}
    >
      {selectable && (
        // The checkbox selects, the rest of the row opens the lead.
        <Checkbox
          checked={selected}
          onChange={onToggleSelect}
          onClick={event => event.stopPropagation()}
          onMouseDown={event => event.stopPropagation()}
          disableRipple
          size="small"
          sx={{ p: 0.5, mt: 0.5 }}
        />
      )}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          '& .MuiListItemText-primary, & .MuiTypography-body1': {
            fontSize: 14,
          },
          '& .MuiListItemText-secondary': { fontSize: 12 },
        }}
      >
        {effectiveUser != null && (
          <UserCard
            user={effectiveUser}
            condensed
            alignItems="flex-start"
            nameAdornment={unprocessedEnquiryChip}
            secondaryText={
              <Box
                component="span"
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  gap: 0.25,
                  color: 'text.secondary',
                }}
              >
                {statusChip != null && (
                  <Box component="span" sx={statusPillSx(statusChip.color)}>
                    {statusChip.label}
                  </Box>
                )}
                <Box
                  component="span"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  <SourceIcon size={13} />
                  {source.label}
                  {lead.lead_stage?.label != null && (
                    <>
                      <Box component="span">·</Box>
                      {lead.lead_stage.label}
                    </>
                  )}
                </Box>
                <Tooltip
                  arrow
                  placement="top"
                  title={
                    <Stack spacing={0.5}>
                      <Box component="span">
                        {t('Created')}: {formatDate(lead.created_at)}
                      </Box>
                      <Box component="span">
                        {t('Updated')}: {formatDate(lead.updated_at)}
                      </Box>
                    </Stack>
                  }
                >
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      width: 'fit-content',
                      cursor: 'help',
                    }}
                  >
                    <Clock size={12} />
                    {t('Updated')}{' '}
                    <TimeAgo
                      dateString={lead.updated_at}
                      addSuffix
                      disableTooltip
                    />
                  </Box>
                </Tooltip>
              </Box>
            }
            userActions={<ActivityLine lead={lead} />}
          />
        )}
      </Box>
    </ListItemButton>
  );
};

const ActivityLine: React.FC<{ lead: BuyerLeadDashboardRowFragment }> = ({
  lead,
}) => {
  const { t, dateLocale } = useLocale();
  const theme = useTheme();
  const palette = (theme.vars ?? theme).palette;

  const next = lead.next_activity[0];
  const last = lead.last_activity[0];
  const activity = next ?? last;
  const isNext = next != null;

  if (activity == null) {
    return (
      <Stack
        direction="row"
        sx={{ alignItems: 'center', gap: 0.5, color: palette.error.main }}
      >
        <TriangleAlert size={14} />
        <Typography variant="caption">{t('No activity')}</Typography>
      </Stack>
    );
  }

  const { Icon, label } = describeActivity(activity, t);
  const dateValue = isNext
    ? activity.consolidated_due_at ?? activity.due_at ?? activity.start_date
    : activity.consolidated_done_at;
  const status = getActivityStatus(
    activity,
    isNext,
    activity.consolidated_due_at,
  );
  const statusColor: Record<ActivityStatus, string> = {
    pending: palette.info.main,
    overdue: palette.warning.main,
    done: palette.success.main,
    cancelled: palette.text.disabled,
  };
  const color =
    activity.done &&
    activity.activity_type === Activity_Types_Enum_Enum.Call &&
    activity.success === false
      ? palette.error.main
      : statusColor[status];

  return (
    <Stack sx={{ alignItems: 'flex-end', maxWidth: 220 }}>
      <Stack direction="row" sx={{ alignItems: 'center', gap: 0.5, color }}>
        <Icon size={14} style={{ flexShrink: 0 }} />
        <Typography variant="caption" noWrap sx={{ maxWidth: 180 }}>
          {label}
        </Typography>
      </Stack>
      {dateValue != null && (
        <Typography variant="caption" sx={{ color }}>
          {formatDistanceStrict(new Date(dateValue), new Date(), {
            addSuffix: true,
            locale: dateLocale,
          })}
        </Typography>
      )}
    </Stack>
  );
};

const describeActivity = (
  activity: BuyerLeadDashboardActivityFragment,
  t: Translate,
): { Icon: LucideIcon; label: string } => {
  const trimmedNote = activityNotePlainText(activity.note);
  const trimmedSubject = activity.subject?.trim();
  switch (activity.activity_type) {
    case Activity_Types_Enum_Enum.Task:
      return {
        Icon: ClipboardCheck,
        label:
          trimmedSubject != null && trimmedSubject !== ''
            ? trimmedSubject
            : t('Task'),
      };
    case Activity_Types_Enum_Enum.Call:
      return {
        Icon: activity.success === false && activity.done ? PhoneMissed : Phone,
        label: trimmedNote !== '' ? trimmedNote : t('Call'),
      };
    case Activity_Types_Enum_Enum.Visit:
      return {
        Icon:
          activity.success === false && activity.done ? DoorClosed : DoorOpen,
        label: trimmedNote !== '' ? trimmedNote : t('Visit'),
      };
    case Activity_Types_Enum_Enum.Note:
      return {
        Icon: StickyNote,
        label: trimmedNote !== '' ? trimmedNote : t('Note'),
      };
    case Activity_Types_Enum_Enum.Assignment:
      return { Icon: UserCheck, label: t('Assignment') };
    default:
      return { Icon: StickyNote, label: t('Activity') };
  }
};

type ActivityStatus = 'pending' | 'overdue' | 'done' | 'cancelled';

const getActivityStatus = (
  activity: BuyerLeadDashboardActivityFragment,
  isNext: boolean,
  dueValue: string | null | undefined,
): ActivityStatus => {
  if (isNext) {
    return dueValue != null && new Date(dueValue).getTime() < Date.now()
      ? 'overdue'
      : 'pending';
  }
  return activity.success === false ? 'cancelled' : 'done';
};

// Side drawer listing the buyer leads that landed in a single matching batch
// (all matching-source leads created on the same day). Supports selecting leads
// (individually or all) to run a bulk action (email / move to column) on them.
const MatchingBatchDrawer: React.FC<{
  batch: MatchingBatch | null;
  onClose: () => void;
  unprocessedLeadIds: Set<string>;
  onOpenLead: (leadId: string) => void;
  parent: React.ComponentProps<typeof ActionsMenu>['parent'];
  stageColumns: { id: string; label: string; status: string | null }[];
  matchedStageId: string | undefined;
  readOnly?: boolean;
}> = ({
  batch,
  onClose,
  unprocessedLeadIds,
  onOpenLead,
  parent,
  stageColumns,
  matchedStageId,
  readOnly = false,
}) => {
  const { t, dateLocale } = useLocale();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // Reset selection whenever a different batch is opened (or the drawer closes).
  const batchKey = batch?.key ?? null;
  useEffect(() => {
    setSelectedIds([]);
  }, [batchKey]);

  const leads = useMemo(() => batch?.leads ?? [], [batch]);
  const selectedLeads = useMemo(
    () =>
      leads
        .filter(lead => selectedIds.includes(lead.id))
        .map(lead => ({
          id: lead.id,
          stage_id: lead.stage_id,
          user: getEffectiveUser(lead),
        })),
    [leads, selectedIds],
  );
  const allSelected = leads.length > 0 && selectedIds.length === leads.length;
  const someSelected = selectedIds.length > 0;

  const toggleSelect = (id: string) =>
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id],
    );
  const toggleSelectAll = () =>
    setSelectedIds(allSelected ? [] : leads.map(lead => lead.id));
  const clearSelection = () => setSelectedIds([]);
  return (
    <Drawer
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            width: { xs: '100%', sm: 440 },
          },
        },
      }}
    >
      {batch != null && (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <DrawerTopBar
            title={getBuyerLeadSourceMeta(batch.source, t).label}
            actionsLoading={false}
            onClose={onClose}
          />
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t('{{count}} buyers matched', { count: batch.leads.length })}
              {' · '}
              {format(new Date(batch.date), 'PPP', { locale: dateLocale })}
            </Typography>
          </Box>
          {!readOnly && batch.leads.length > 0 && (
            <>
              <Divider />
              <Box
                sx={{
                  px: 2,
                  py: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  justifyContent: 'space-between',
                  minHeight: 44,
                }}
              >
                <ListItemButton
                  onClick={toggleSelectAll}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    p: 0,
                    flex: 'none',
                    cursor: 'pointer',
                  }}
                >
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected && !allSelected}
                    tabIndex={-1}
                    disableRipple
                    size="small"
                    sx={{ p: 0.5 }}
                  />
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {someSelected
                      ? t('{{count}} selected', {
                          count: selectedIds.length,
                        })
                      : t('Select all')}
                  </Typography>
                </ListItemButton>
                {someSelected && (
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ alignItems: 'center' }}
                  >
                    <Suspense fallback={null}>
                      <ActionsMenu
                        selectedLeads={selectedLeads}
                        columns={stageColumns}
                        parent={parent}
                        onReset={clearSelection}
                        matchedBulkEmailStageId={matchedStageId}
                      />
                    </Suspense>
                    <Button
                      variant="text"
                      size="small"
                      onClick={clearSelection}
                    >
                      {t('Cancel')}
                    </Button>
                  </Stack>
                )}
              </Box>
            </>
          )}
          <Divider />
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            <Stack divider={<Divider />}>
              {batch.leads.map(lead => (
                <BuyerLeadRow
                  key={lead.id}
                  lead={lead}
                  stageStatus={
                    stageColumns.find(stage => stage.id === lead.stage_id)
                      ?.status
                  }
                  hasUnprocessedEnquiry={unprocessedLeadIds.has(lead.id)}
                  selectable={!readOnly}
                  selected={selectedIds.includes(lead.id)}
                  onToggleSelect={() => toggleSelect(lead.id)}
                  onClick={() => onOpenLead(lead.id)}
                />
              ))}
            </Stack>
          </Box>
        </Box>
      )}
    </Drawer>
  );
};

const EMAIL_STATUS_ORDER: EmailStatus[] = ['sent', 'error', 'pending'];

const getEmailStatusMeta = (
  status: EmailStatus,
  t: Translate,
): { label: string; color: 'success' | 'error' | 'warning' } => {
  switch (status) {
    case 'sent':
      return { label: t('Sent'), color: 'success' };
    case 'error':
      return { label: t('Failed'), color: 'error' };
    case 'pending':
      return { label: t('Pending'), color: 'warning' };
  }
};

// Compact per-status count pills shown on a campaign row / drawer header.
const EmailStatusCounts: React.FC<{ counts: Record<EmailStatus, number> }> = ({
  counts,
}) => {
  const { t } = useLocale();
  const visible = EMAIL_STATUS_ORDER.filter(status => counts[status] > 0);
  if (visible.length === 0) {
    return null;
  }
  return (
    <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.5, mt: 0.25 }}>
      {visible.map(status => {
        const meta = getEmailStatusMeta(status, t);
        return (
          <Box key={status} component="span" sx={statusPillSx(meta.color)}>
            {counts[status]} {meta.label}
          </Box>
        );
      })}
    </Stack>
  );
};

const EmailStatusChip: React.FC<{ status: string | null }> = ({ status }) => {
  const { t } = useLocale();
  if (status == null) {
    return null;
  }
  if (status === 'sent' || status === 'error' || status === 'pending') {
    const meta = getEmailStatusMeta(status, t);
    return (
      <Box component="span" sx={statusPillSx(meta.color)}>
        {meta.label}
      </Box>
    );
  }
  // Unknown/other status — surface it raw rather than hiding it.
  return (
    <Box component="span" sx={{ fontSize: 11, color: 'text.secondary' }}>
      {status}
    </Box>
  );
};

// The address we actually emailed lives on `email_message.to` (a jsonb array of
// `{ name, email }`), which is authoritative for a mass-mail report. Fall back
// to the user's stored email only if the message somehow has no recipient.
const getCampaignRecipientEmail = (
  activity: ListingEmailCampaignActivityFragment,
): string | null => {
  const to = activity.email_message?.to;
  if (Array.isArray(to)) {
    const first = to[0] as { email?: unknown } | undefined;
    if (
      first != null &&
      typeof first.email === 'string' &&
      first.email !== ''
    ) {
      return first.email;
    }
  }
  return activity.buyer_lead != null
    ? getEffectiveUser(activity.buyer_lead)?.emails[0]?.email ?? null
    : null;
};

// Side drawer listing every recipient of a single mass-email campaign, with the
// per-recipient delivery status.
const EmailCampaignDrawer: React.FC<{
  campaign: EmailCampaignBatch | null;
  lotId: string;
  onClose: () => void;
  onOpenLead: (leadId: string) => void;
}> = ({ campaign, lotId, onClose, onOpenLead }) => {
  const { t, dateLocale } = useLocale();
  const { data, error, loading } = useQuery<
    EmailCampaignRecipientsData,
    { lotId: string; campaignId: string }
  >(GET_LISTING_EMAIL_CAMPAIGN_RECIPIENTS, {
    variables: {
      lotId,
      campaignId: campaign?.campaignId ?? lotId,
    },
    skip: campaign == null,
    fetchPolicy: 'network-only',
  });
  const activities = data?.activities ?? [];
  const subject = campaign?.subject ?? null;

  return (
    <Drawer
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            width: { xs: '100%', sm: 440 },
          },
        },
      }}
    >
      {campaign != null && (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <DrawerTopBar
            title={t('Mass email')}
            actionsLoading={loading}
            onClose={onClose}
          />
          <Box sx={{ px: 2, py: 1.5 }}>
            {subject != null && (
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }} noWrap>
                {subject}
              </Typography>
            )}
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t('{{count}} emails', { count: campaign.activityCount })}
              {' · '}
              {format(new Date(campaign.date), 'PPP', { locale: dateLocale })}
            </Typography>
            <EmailStatusCounts counts={campaign.counts} />
          </Box>
          <Divider />
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            {error != null && (
              <Alert severity="error" sx={{ m: 2 }}>
                {error.message}
              </Alert>
            )}
            <Stack divider={<Divider />}>
              {activities.map(activity => {
                const user =
                  activity.buyer_lead != null
                    ? getEffectiveUser(activity.buyer_lead)
                    : null;
                const leadId = activity.buyer_lead?.id;
                const recipientEmail = getCampaignRecipientEmail(activity);
                const statusChip = (
                  <EmailStatusChip
                    status={activity.email_message?.status ?? null}
                  />
                );
                return (
                  <ListItemButton
                    key={activity.id}
                    disabled={leadId == null}
                    onClick={
                      leadId != null ? () => onOpenLead(leadId) : undefined
                    }
                    sx={{
                      px: 2,
                      py: 1,
                      cursor: leadId != null ? 'pointer' : 'default',
                      '&:hover':
                        leadId != null
                          ? { bgcolor: 'action.hover' }
                          : undefined,
                      '&.Mui-disabled': { opacity: 1 },
                    }}
                  >
                    {user != null ? (
                      <Box
                        sx={{
                          '& .MuiListItemText-primary, & .MuiTypography-body1':
                            {
                              fontSize: 14,
                            },
                          '& .MuiListItemText-secondary': { fontSize: 12 },
                        }}
                      >
                        <UserCard
                          user={user}
                          condensed
                          userActions={statusChip}
                          secondaryText={recipientEmail}
                        />
                      </Box>
                    ) : (
                      <Stack
                        direction="row"
                        sx={{
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 1,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ color: 'text.secondary' }}
                        >
                          {recipientEmail ?? t('Unknown recipient')}
                        </Typography>
                        {statusChip}
                      </Stack>
                    )}
                  </ListItemButton>
                );
              })}
            </Stack>
          </Box>
        </Box>
      )}
    </Drawer>
  );
};
